/**
 * System Health Page
 *
 * Real-time system health monitoring dashboard.
 */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnalyticsSubNav } from '../components/layout/AnalyticsLayout';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import api from '../services/api';
import { useIsPro } from '../hooks/useSubscription';
import { ProUpgradeCard } from '../components/billing/ProUpgradeCard';

/**
 * Service health status
 */
type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

/**
 * Individual service health
 */
interface ServiceHealth {
  name: string;
  status: HealthStatus;
  latency?: number;
  message?: string;
  lastChecked: string;
  details?: Record<string, unknown>;
}

/**
 * System health response
 */
interface SystemHealthResponse {
  status: HealthStatus;
  timestamp: string;
  version: string;
  uptime: number;
  services: ServiceHealth[];
}

/**
 * Fetch system health
 */
async function fetchSystemHealth(): Promise<SystemHealthResponse> {
  const response = await api.get<SystemHealthResponse>('/health/detailed');
  return response.data;
}

/**
 * Status indicator component
 */
function StatusIndicator({ status }: { status: HealthStatus }) {
  const styles = {
    healthy: 'bg-green-500',
    degraded: 'bg-yellow-500',
    unhealthy: 'bg-red-500',
    unknown: 'bg-gray-400',
  };

  const pulseStyles = {
    healthy: 'animate-pulse bg-green-400',
    degraded: 'animate-pulse bg-yellow-400',
    unhealthy: 'animate-pulse bg-red-400',
    unknown: '',
  };

  return (
    <div className="relative flex h-3 w-3">
      <span
        className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${pulseStyles[status]}`}
      />
      <span
        className={`relative inline-flex rounded-full h-3 w-3 ${styles[status]}`}
      />
    </div>
  );
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: HealthStatus }) {
  const styles = {
    healthy: 'bg-green-100 text-green-800 border-green-200',
    degraded: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    unhealthy: 'bg-red-100 text-red-800 border-red-200',
    unknown: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  const labels = {
    healthy: 'Healthy',
    degraded: 'Degraded',
    unhealthy: 'Unhealthy',
    unknown: 'Unknown',
  };

  return (
    <span
      className={`px-3 py-1 text-sm font-medium rounded-full border ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

/**
 * Service icon based on name
 */
function getServiceIcon(name: string): string {
  const icons: Record<string, string> = {
    api: '🌐',
    database: '🗄️',
    postgres: '🐘',
    redis: '🔴',
    celery: '🥬',
    worker: '⚙️',
    queue: '📬',
    storage: '💾',
    auth: '🔐',
    cache: '💨',
  };

  const lowerName = name.toLowerCase();
  for (const [key, icon] of Object.entries(icons)) {
    if (lowerName.includes(key)) {
      return icon;
    }
  }
  return '📦';
}

/**
 * Format uptime duration
 */
function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.length > 0 ? parts.join(' ') : '< 1m';
}

/**
 * Service Card Component
 */
function ServiceCard({ service }: { service: ServiceHealth }) {
  const icon = getServiceIcon(service.name);

  return (
    <Card
      className={`transition-all ${
        service.status === 'unhealthy'
          ? 'border-red-200 bg-red-50/50'
          : service.status === 'degraded'
          ? 'border-yellow-200 bg-yellow-50/50'
          : ''
      }`}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-2xl">
              {icon}
            </div>
            <div>
              <h3 className="font-semibold">{service.name}</h3>
              <p className="text-xs text-muted-foreground">
                Last checked:{' '}
                {new Date(service.lastChecked).toLocaleTimeString()}
              </p>
            </div>
          </div>
          <StatusIndicator status={service.status} />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Status</span>
            <StatusBadge status={service.status} />
          </div>

          {service.latency !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Latency</span>
              <span
                className={`text-sm font-medium ${
                  service.latency < 100
                    ? 'text-green-600'
                    : service.latency < 500
                    ? 'text-yellow-600'
                    : 'text-red-600'
                }`}
              >
                {service.latency}ms
              </span>
            </div>
          )}

          {service.message && (
            <div className="mt-3 p-2 bg-slate-100 rounded text-xs text-muted-foreground">
              {service.message}
            </div>
          )}

          {service.details && Object.keys(service.details).length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                Show details
              </summary>
              <pre className="mt-2 p-2 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto">
                {JSON.stringify(service.details, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * System Health Page Component
 */
export default function SystemHealth() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds
  const { isPro, isLoading: planLoading } = useIsPro();

  const { data, isLoading, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['systemHealth'],
    queryFn: fetchSystemHealth,
    refetchInterval: autoRefresh && isPro ? refreshInterval : false,
    staleTime: 10000,
    enabled: isPro,
  });

  // These three must sit ABOVE the non-Pro early return. They used to live
  // below it, so a free user rendered three fewer hooks than a Pro one — and
  // the moment isPro resolved from undefined to true, React saw the hook count
  // change mid-component and threw "Rendered more hooks than during the
  // previous render". Caught by react-hooks/rules-of-hooks once ESLint was
  // made to run at all. (Aug 14 2026)
  // Countdown to next refresh
  const [countdown, setCountdown] = useState(refreshInterval / 1000);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return refreshInterval / 1000;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, dataUpdatedAt]);

  // Reset countdown when data updates
  useEffect(() => {
    setCountdown(refreshInterval / 1000);
  }, [dataUpdatedAt, refreshInterval]);

  if (!planLoading && !isPro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <AnalyticsSubNav />
          <ProUpgradeCard
            feature="System health"
            description="Live service, queue and error-rate monitoring. Upgrade to Pro ($29/mo) to unlock advanced analytics."
          />
        </div>
      </div>
    );
  }

  // Calculate overall status
  const getOverallStatus = (): HealthStatus => {
    if (!data?.services) return 'unknown';
    if (data.services.some((s) => s.status === 'unhealthy')) return 'unhealthy';
    if (data.services.some((s) => s.status === 'degraded')) return 'degraded';
    if (data.services.every((s) => s.status === 'healthy')) return 'healthy';
    return 'unknown';
  };

  const overallStatus = data ? getOverallStatus() : 'unknown';
  const healthyCount = data?.services.filter((s) => s.status === 'healthy').length || 0;
  const totalCount = data?.services.length || 0;

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">❌</div>
              <CardTitle className="mb-2">Error Loading System Health</CardTitle>
              <CardDescription>
                {error instanceof Error ? error.message : 'An error occurred'}
              </CardDescription>
              <Button className="mt-4" variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-7xl mx-auto py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">System Health</h1>
            <p className="text-muted-foreground">
              Monitor service status and performance
            </p>
          </div>
          <AnalyticsSubNav />
        </div>

        {/* Overall Status Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl ${
                    overallStatus === 'healthy'
                      ? 'bg-green-100'
                      : overallStatus === 'degraded'
                      ? 'bg-yellow-100'
                      : overallStatus === 'unhealthy'
                      ? 'bg-red-100'
                      : 'bg-gray-100'
                  }`}
                >
                  {overallStatus === 'healthy'
                    ? '✅'
                    : overallStatus === 'degraded'
                    ? '⚠️'
                    : overallStatus === 'unhealthy'
                    ? '❌'
                    : '❓'}
                </div>
                <div>
                  <h2 className="text-xl font-bold">
                    {overallStatus === 'healthy'
                      ? 'All Systems Operational'
                      : overallStatus === 'degraded'
                      ? 'Some Systems Degraded'
                      : overallStatus === 'unhealthy'
                      ? 'System Issues Detected'
                      : 'Checking Status...'}
                  </h2>
                  <p className="text-muted-foreground">
                    {healthyCount} of {totalCount} services healthy
                    {data?.uptime && ` • Uptime: ${formatUptime(data.uptime)}`}
                    {data?.version && ` • Version: ${data.version}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Auto-refresh controls */}
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      className="rounded"
                    />
                    Auto-refresh
                  </label>
                  {autoRefresh && (
                    <span className="text-xs text-muted-foreground">
                      ({countdown}s)
                    </span>
                  )}
                </div>

                <select
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(Number(e.target.value))}
                  className="text-sm border rounded px-2 py-1"
                  disabled={!autoRefresh}
                >
                  <option value={10000}>10s</option>
                  <option value={30000}>30s</option>
                  <option value={60000}>1m</option>
                  <option value={300000}>5m</option>
                </select>

                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Refresh Now
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <LoadingSkeleton className="w-12 h-12 rounded-lg" />
                    <div>
                      <LoadingSkeleton className="h-5 w-24 mb-1" />
                      <LoadingSkeleton className="h-3 w-32" />
                    </div>
                  </div>
                  <LoadingSkeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : data?.services && data.services.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.services.map((service) => (
              <ServiceCard key={service.name} service={service} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">🔍</div>
              <CardTitle className="mb-2">No Services Found</CardTitle>
              <CardDescription>
                No services are reporting health status
              </CardDescription>
            </CardContent>
          </Card>
        )}

        {/* Last Updated */}
        {data && (
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Last updated: {new Date(data.timestamp).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}
